// Aviso Legal / Legal Notice — required by Spanish law (LSSI-CE, Ley 34/2002)
// for any commercial website operated from Spain. Placeholders are marked
// with «···» / "TBD" until the business is formally registered. Rendered
// in English when the language switch is set to `eng`, otherwise Spanish
// (Spain still legally requires the Spanish version).

import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { useLang } from "../lib/i18n";

export default function LegalNoticePage() {
  const lang = useLang();
  const es = lang === "spa";
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 fade-up" data-testid="legal-notice-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252] mb-4 inline-flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> {es ? "Aviso Legal" : "Legal Notice"}
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tracking-tight mb-3">
        {es ? "Aviso Legal" : "Legal Notice"}
      </h1>
      <p className="text-[#4A4030] text-lg">
        {es
          ? "Información obligatoria conforme a la Ley 34/2002 de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI-CE)."
          : "Mandatory information under Spain's Ley 34/2002 on Information Society Services and E-Commerce (LSSI-CE)."}
      </p>

      <div className="mt-12 space-y-8 text-[#0A0A0A] leading-relaxed">
        {es ? <SpanishNotice /> : <EnglishNotice />}
        <p className="text-sm text-[#525252] pt-6 border-t border-[rgba(0,0,0,0.08)]">
          {es ? "Última actualización" : "Last updated"}: {es ? "20 de julio de 2026" : "20 July 2026"}.
        </p>
      </div>
    </div>
  );
}

function EnglishNotice() {
  return (
    <>
      <Section title="1. Owner details">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li><strong>Owner:</strong> Reads (project in registration phase).</li>
          <li><strong>Legal representative:</strong> &ldquo;Pending registration&rdquo;</li>
          <li><strong>Tax ID (NIF / DNI):</strong> &ldquo;Pending&rdquo;</li>
          <li><strong>Registered address:</strong> &ldquo;Pending registration&rdquo;</li>
          <li><strong>Contact email:</strong> readscoteam@gmail.com</li>
          <li><strong>Website:</strong> https://readsco.org</li>
        </ul>
        <p className="mt-3 text-sm text-[#525252]">Note: Reads is currently operated as an individual (natural person) project. Full tax details will be published once the business is formally registered.</p>
      </Section>

      <Section title="2. Purpose">
        This Legal Notice governs use of the website <em>readsco.org</em> (hereafter &ldquo;Reads&rdquo; or &ldquo;the Site&rdquo;), a personal reading diary that lets users log books, rate them, and receive personalised recommendations.
      </Section>

      <Section title="3. Terms of use">
        Access to the Site is free except for features explicitly marked as part of the Reads+ subscription, whose terms are detailed in the <Link to="/legal/condiciones" className="underline">Terms of Service</Link>.
        Users agree to use Reads lawfully, respecting third-party rights and refraining from posting illegal, offensive, or copyright-infringing content.
      </Section>

      <Section title="4. Intellectual property">
        All content on the Site (text, graphic design, source code, logos) is the exclusive property of Reads or third parties who have granted a licence, and is protected by intellectual and industrial property law. Reproduction, distribution or modification without express authorisation is prohibited.
        Book covers and bibliographic metadata come from <em>Open Library</em> under an open licence.
      </Section>

      <Section title="5. Liability">
        Reads is not responsible for user-generated content in the form of reviews, nor for the accuracy of bibliographic data supplied by third parties. We reserve the right to remove any content we deem contrary to the law or the spirit of the service.
      </Section>

      <Section title="6. Data protection">
        Processing of personal data is governed by the <Link to="/legal/privacidad" className="underline">Privacy Policy</Link>, available on this same site. The data controller is the project owner named above. On registration, Reads records the IP address and browser user-agent as part of its abuse-prevention system (art. 6.1.f GDPR, legitimate interest). Users may exercise their GDPR rights (access, rectification, erasure, portability, restriction, objection) by emailing <a href="mailto:readscoteam@gmail.com" className="underline">readscoteam@gmail.com</a>. Complaints may be filed with the Spanish Data Protection Authority (AEPD) at <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="underline">www.aepd.es</a>.
      </Section>

      <Section title="7. Cookies">
        Reads uses only strictly-necessary cookies (session, authentication and language preference). No consent is required for these under art. 22.2 LSSI-CE. Full details are set out in the <Link to="/legal/cookies" className="underline">Cookies Policy</Link>.
      </Section>

      <Section title="8. Online dispute resolution">
        EU consumers may also file complaints via the European Commission&rsquo;s ODR platform: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline">ec.europa.eu/consumers/odr</a>.
      </Section>

      <Section title="9. Governing law">
        This Legal Notice is governed by Spanish law. Any dispute arising from use of the Site will be submitted to the courts of the consumer&rsquo;s domicile in accordance with article 90.2 of Royal Legislative Decree 1/2007.
      </Section>
    </>
  );
}

function SpanishNotice() {
  return (
    <>
      <Section title="1. Datos del titular">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li><strong>Titular:</strong> Reads (proyecto en fase de registro).</li>
          <li><strong>Nombre y apellidos del responsable:</strong> «Pendiente de registrar»</li>
          <li><strong>NIF / DNI:</strong> «Pendiente»</li>
          <li><strong>Domicilio:</strong> «Pendiente de registrar»</li>
          <li><strong>Correo de contacto:</strong> readscoteam@gmail.com</li>
          <li><strong>Sitio web:</strong> https://readsco.org</li>
        </ul>
        <p className="mt-3 text-sm text-[#525252]">Nota: Reads se encuentra actualmente operando como proyecto de persona física. Los datos fiscales completos se publicarán una vez formalizado el registro.</p>
      </Section>

      <Section title="2. Objeto">
        El presente Aviso Legal regula el uso del sitio web <em>readsco.org</em> (en adelante, &ldquo;Reads&rdquo; o &ldquo;el Sitio&rdquo;), un diario personal de lectura que permite a las personas usuarias registrar libros, valorarlos y recibir recomendaciones personalizadas.
      </Section>

      <Section title="3. Condiciones de uso">
        El acceso al Sitio es gratuito salvo en las funciones expresamente indicadas como parte del servicio Reads+, cuyos términos se encuentran detallados en las <Link to="/legal/condiciones" className="underline">Condiciones Generales de Contratación</Link>.
        La persona usuaria se compromete a hacer un uso lícito de Reads, respetando los derechos de terceros y sin publicar contenidos ilegales, ofensivos o que infrinjan derechos de autor.
      </Section>

      <Section title="4. Propiedad intelectual">
        Todos los contenidos del Sitio (textos, diseño gráfico, código fuente, logotipos) son titularidad exclusiva de Reads o de terceros que han cedido su uso, y están protegidos por la legislación de propiedad intelectual e industrial. Queda prohibida su reproducción, distribución o modificación sin autorización expresa.
        Las portadas y metadatos bibliográficos proceden de <em>Open Library</em> bajo licencia abierta.
      </Section>

      <Section title="5. Responsabilidad">
        Reads no se hace responsable de los contenidos publicados por las personas usuarias en forma de reseñas, ni de la exactitud de los datos bibliográficos aportados por terceros. Nos reservamos el derecho a retirar cualquier contenido que consideremos contrario a la ley o al espíritu del servicio.
      </Section>

      <Section title="6. Protección de datos">
        El tratamiento de los datos personales se rige por la <Link to="/legal/privacidad" className="underline">Política de Privacidad</Link>, disponible en este mismo sitio. El responsable del tratamiento es el titular del proyecto arriba indicado. En el momento del registro, Reads guarda la dirección IP y la cadena user-agent del navegador como parte del sistema de prevención de abuso (art. 6.1.f RGPD, interés legítimo). Las personas usuarias pueden ejercer sus derechos RGPD (acceso, rectificación, supresión, portabilidad, limitación y oposición) escribiendo a <a href="mailto:readscoteam@gmail.com" className="underline">readscoteam@gmail.com</a>. Asimismo, pueden presentar reclamación ante la Agencia Española de Protección de Datos (AEPD): <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="underline">www.aepd.es</a>.
      </Section>

      <Section title="7. Cookies">
        Reads utiliza únicamente cookies estrictamente necesarias (sesión, autenticación y preferencia de idioma). Conforme al artículo 22.2 de la LSSI-CE, estas cookies no requieren consentimiento. Los detalles completos se recogen en la <Link to="/legal/cookies" className="underline">Política de Cookies</Link>.
      </Section>

      <Section title="8. Resolución de litigios en línea">
        Las personas consumidoras residentes en la UE pueden acudir a la plataforma europea de resolución de litigios: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline">ec.europa.eu/consumers/odr</a>.
      </Section>

      <Section title="9. Legislación aplicable">
        Este Aviso Legal se rige por la legislación española. Cualquier controversia derivada del uso del Sitio se someterá a los tribunales del domicilio de la persona consumidora conforme al artículo 90.2 del Real Decreto Legislativo 1/2007.
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-2xl md:text-3xl text-[#0A0A0A] tracking-tight mb-3">{title}</h2>
      <div className="text-[15px] text-[#0A0A0A]">{children}</div>
    </section>
  );
}
